/* ============================================================
   LOX & CHAIN — Product gallery
   Desktop: injects custom prev/next arrows (.lc-pdp-arrow) onto
   .splide--product and wires them to scrollBy the .splide__list.
   Mobile: re-enables swipe when a video slide is present (the theme
   disables Splide drag in that case). Also fixes video looping.

   All critical styles are applied inline with !important so no
   CSS / Tailwind / Alpine rule can override them.

   Companion CSS lives in custom.css under the
   "PDP DESKTOP — Horizontal carousel + unified scroll" block.

   May 2026 update: respects `body.engraving-popup-open` — when the
   engraving customizer popup is open, the arrows are force-hidden
   so they don't poke through the overlay. As soon as the popup
   closes, normal updateState logic kicks back in.
   ============================================================ */
(function () {
  if (window.__loxcPdpCarouselInit) return;
  window.__loxcPdpCarouselInit = true;

  var PREV_SVG = '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:block;pointer-events:none"><polyline points="15 6 9 12 15 18"/></svg>';
  var NEXT_SVG = '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:block;pointer-events:none"><polyline points="9 6 15 12 9 18"/></svg>';

  function isDesktop() {
    return window.matchMedia('(min-width: 1024px)').matches;
  }

  function setImp(el, prop, val) {
    el.style.setProperty(prop, val, 'important');
  }

  // True when the engraving popup is currently open. The engraving
  // manager toggles `engraving-popup-open` on <body> whenever the popup
  // shows/hides; we watch that class to decide whether to suppress arrows.
  function engravingPopupIsOpen() {
    return document.body.classList.contains('engraving-popup-open');
  }

  // ---- Desktop: custom prev/next arrows ----

  function makeArrow(direction) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'lc-pdp-arrow lc-pdp-arrow--' + direction;
    btn.setAttribute('aria-label', direction === 'prev' ? 'Previous image' : 'Next image');
    btn.innerHTML = direction === 'prev' ? PREV_SVG : NEXT_SVG;

    // Flat chevron — no circular container, just the icon with a
    // subtle drop-shadow so it stays readable over any photo.
    setImp(btn, 'position', 'absolute');
    setImp(btn, 'top', '50%');
    setImp(btn, 'transform', 'translateY(-50%)');
    setImp(btn, 'width', '40px');
    setImp(btn, 'height', '40px');
    setImp(btn, 'background', 'transparent');
    setImp(btn, 'border', 'none');
    setImp(btn, 'box-shadow', 'none');
    setImp(btn, 'border-radius', '0');
    setImp(btn, 'display', 'flex');
    setImp(btn, 'align-items', 'center');
    setImp(btn, 'justify-content', 'center');
    setImp(btn, 'cursor', 'pointer');
    setImp(btn, 'z-index', '50');
    setImp(btn, 'padding', '0');
    setImp(btn, 'margin', '0');
    setImp(btn, 'opacity', '1');
    setImp(btn, 'visibility', 'visible');
    setImp(btn, 'pointer-events', 'auto');
    setImp(btn, 'color', '#000');
    setImp(btn, 'filter', 'drop-shadow(0 0 8px #ffffffe4) drop-shadow(0 0 8px #ffffffc3) drop-shadow(0 0 8px #ffffffb1) drop-shadow(0 0 50px #ffffffb5)');
    setImp(btn, 'transition', 'transform 160ms ease, opacity 160ms ease');
    if (direction === 'prev') setImp(btn, 'left', '14px');
    else setImp(btn, 'right', '14px');
    return btn;
  }

  function wire(splide) {
    if (!splide || splide.dataset.lcWired === '1') return;

    var list = splide.querySelector('.splide__list');
    if (!list) return;

    splide.dataset.lcWired = '1';

    // Force the splide container to be a regular positioned parent
    // (non-sticky), beating Tailwind's lg:!sticky. We want the image
    // to scroll with the page as one unit, not stay pinned.
    setImp(splide, 'position', 'relative');
    setImp(splide, 'top', 'auto');

    var prev = makeArrow('prev');
    var next = makeArrow('next');
    splide.appendChild(prev);
    splide.appendChild(next);

    function step() {
      var first = list.querySelector('.splide__slide');
      return first ? first.getBoundingClientRect().width : list.clientWidth;
    }

    function updateState() {
      // If the engraving popup is open, suppress arrows entirely —
      // they live in the same stacking context as the product images
      // and would otherwise poke through the popup overlay.
      if (engravingPopupIsOpen()) {
        setImp(prev, 'display', 'none');
        setImp(next, 'display', 'none');
        return;
      }
      var max = list.scrollWidth - list.clientWidth - 2;
      var hasOverflow = list.scrollWidth > list.clientWidth + 2;
      var atStart = list.scrollLeft <= 2;
      var atEnd = list.scrollLeft >= max;
      // Hide each arrow at its respective extreme. Also hide both
      // entirely if there's only one image (no overflow).
      setImp(prev, 'display', (hasOverflow && !atStart) ? 'flex' : 'none');
      setImp(next, 'display', (hasOverflow && !atEnd) ? 'flex' : 'none');
    }

    prev.addEventListener('click', function () {
      list.scrollBy({ left: -step(), behavior: 'smooth' });
    });
    next.addEventListener('click', function () {
      list.scrollBy({ left: step(), behavior: 'smooth' });
    });

    list.addEventListener('scroll', updateState, { passive: true });
    window.addEventListener('resize', updateState);

    // Recompute once layout / images have settled.
    setTimeout(updateState, 100);
    setTimeout(updateState, 800);
    setTimeout(updateState, 2000);

    // Store a reference so we can re-trigger updateState on this
    // wired instance when the engraving popup closes.
    splide.__lcUpdateState = updateState;
  }

  // ---- Video fixes (MutationObserver-based) ----
  // Videos live inside <template x-if="enabled"> and are only rendered by
  // Alpine.js on scroll/intersection, so we can't target them synchronously.
  // We observe the DOM and fix each video the moment it appears.

  function applyVideoFixes(video) {
    if (video.dataset.lcVideoFixed) return;
    video.dataset.lcVideoFixed = '1';

    // Force looping on all viewports.
    video.loop = true;
    video.addEventListener('ended', function () {
      video.currentTime = 0;
      video.play().catch(function () {});
    });

    if (!isDesktop()) {
      // Mobile: browser-native video controls ignore pointer-events:none and
      // swallow touch events. Remove controls entirely — the video is already
      // muted+autoplay so controls aren't needed.
      video.removeAttribute('controls');
      video.style.setProperty('pointer-events', 'none', 'important');

      // Also neutralise the immediate Alpine wrapper so swipes reach Splide.
      var island = video.closest('data-island');
      if (island) island.style.setProperty('pointer-events', 'none', 'important');
    }

    // Safari fix: when preload="none" and the video is in a hidden slide at
    // mount time, Safari never loads the media and autoplay silently fails.
    // Calling load() forces Safari to fetch the source; we then play once ready.
    if (video.readyState === 0) {
      video.addEventListener('canplay', function () {
        video.play().catch(function () {});
      }, { once: true });
      video.load();
    }
  }

  var _lcVideoObserver = null;

  function watchForVideos() {
    if (_lcVideoObserver) return;

    // Fix any videos already in the DOM (e.g. from a prior Alpine render).
    document.querySelectorAll(
      '.product-media-container .splide--product video, .splide.splide--product video'
    ).forEach(applyVideoFixes);

    _lcVideoObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.tagName === 'VIDEO') {
            applyVideoFixes(node);
          } else {
            node.querySelectorAll('video').forEach(applyVideoFixes);
          }
        });
      });
    });

    var root = document.querySelector('.product-media-container') || document.body;
    _lcVideoObserver.observe(root, { childList: true, subtree: true });
  }

  // ---- Mobile: custom swipe handler ----
  // When a video slide is present, the theme disables Splide's drag
  // (touch-action: auto on the track). Our handler detects this at touchstart
  // and takes over: it follows the finger during touchmove and navigates on
  // touchend by clicking the appropriate pagination dot.
  //
  // On products without video, Splide's drag is already active (touch-action
  // is pan-y). We still attach listeners but defer our touchend logic with
  // setTimeout(0) so Splide runs first. If Splide navigated (active dot
  // changed), we bail — preventing any double-navigation.

  function addMobileSwipe() {
    var track = document.querySelector('.splide__track');
    if (!track || track.dataset.lcSwipe) return;
    track.dataset.lcSwipe = '1';

    var list = track.querySelector('.splide__list');
    if (!list) return;

    var startX = null, startY = null, baseTranslate = 0,
        startActiveIdx = -1, dragging = false, weHandleDrag = false;

    function getTranslateX() {
      var m = list.style.transform.match(/translateX\((-?[\d.]+)px\)/);
      return m ? parseFloat(m[1]) : 0;
    }

    function slideWidth() {
      var s = list.querySelector('.splide__slide');
      return s ? s.getBoundingClientRect().width : track.clientWidth;
    }

    function getDots() { return document.querySelectorAll('.splide__pagination__page'); }

    function getActiveIdx(dots) {
      for (var i = 0; i < dots.length; i++) {
        if (dots[i].classList.contains('is-active')) return i;
      }
      return -1;
    }

    track.addEventListener('touchstart', function (e) {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      baseTranslate = getTranslateX();
      dragging = false;
      // Detect whether Splide's drag is disabled for this product.
      // The theme sets touch-action:auto on video products, leaving
      // touch-action:pan-y on normal products where Splide handles drag.
      weHandleDrag = !!document.querySelector('[data-product-media-type-video]');
      var dots = getDots();
      startActiveIdx = getActiveIdx(dots);
      if (weHandleDrag) {
        list.style.setProperty('transition', 'none', 'important');
      }
    }, { passive: true });

    track.addEventListener('touchmove', function (e) {
      // Only update transform when we are the drag handler (video products).
      if (!weHandleDrag || startX === null) return;
      var dx = e.touches[0].clientX - startX;
      var dy = e.touches[0].clientY - startY;

      if (!dragging) {
        if (Math.abs(dx) < 8) return;
        // Clearly vertical — cancel our drag and restore transition.
        if (Math.abs(dy) > Math.abs(dx)) {
          startX = null;
          list.style.removeProperty('transition');
          return;
        }
        dragging = true;
      }

      list.style.transform = 'translateX(' + (baseTranslate + dx) + 'px)';
    }, { passive: true });

    track.addEventListener('touchend', function (e) {
      if (startX === null) return;
      if (weHandleDrag && !dragging) { startX = null; return; }

      var dx = e.changedTouches[0].clientX - startX;
      var savedBase = baseTranslate;
      var savedIdx = startActiveIdx;
      var wasOurDrag = weHandleDrag;
      startX = null;
      dragging = false;

      if (Math.abs(dx) < 30) {
        if (wasOurDrag) list.style.removeProperty('transition');
        return;
      }

      // Defer to next task so Splide's own touchend handler runs first.
      // If Splide navigated (active dot changed), we bail — no double-slide.
      setTimeout(function () {
        var dots = getDots();
        var newIdx = getActiveIdx(dots);

        if (newIdx !== savedIdx) {
          // Splide handled it — clean up any transition override.
          if (wasOurDrag) list.style.removeProperty('transition');
          return;
        }

        // Splide didn't navigate — apply our navigation.
        var threshold = Math.min(50, slideWidth() * 0.2);
        var targetDot = null;
        if (dx < -threshold && savedIdx < dots.length - 1) targetDot = dots[savedIdx + 1];
        else if (dx > threshold && savedIdx > 0) targetDot = dots[savedIdx - 1];

        if (wasOurDrag) {
          // Animate the snap to the target slide.
          list.style.setProperty('transition', 'transform 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94)', 'important');
          list.getBoundingClientRect(); // force reflow so transition is registered
        }

        if (targetDot) {
          targetDot.click(); // Splide updates transform → browser animates the snap
        } else if (wasOurDrag) {
          list.style.transform = 'translateX(' + savedBase + 'px)'; // snap back
        }

        if (wasOurDrag) {
          setTimeout(function () { list.style.removeProperty('transition'); }, 350);
        }
      }, 0);
    }, { passive: true });
  }

  // ---- Bootstrap ----

  // On video products the theme sets touch-action:auto which makes the browser
  // handle ALL gestures, causing page scroll during horizontal swipe.
  // Setting pan-y: browser handles vertical scroll, passes horizontal to our handler.
  function fixTouchAction() {
    if (!document.querySelector('[data-product-media-type-video]')) return;
    var track = document.querySelector('.splide__track');
    if (!track) return;
    track.style.setProperty('touch-action', 'pan-y', 'important');
    var list = track.querySelector('.splide__list');
    if (list) list.style.setProperty('touch-action', 'pan-y', 'important');
  }

  function scan() {
    watchForVideos();
    fixTouchAction();
    addMobileSwipe();
    if (!isDesktop()) return;
    document.querySelectorAll(
      '.product-media-container .splide--product, .splide.splide--product'
    ).forEach(wire);
  }

  // Re-run updateState on every wired splide. Used by the engraving
  // popup body-class observer below to refresh arrows on popup close.
  function refreshAllArrows() {
    document.querySelectorAll('.splide--product[data-lc-wired="1"]').forEach(function (splide) {
      if (typeof splide.__lcUpdateState === 'function') splide.__lcUpdateState();
    });
  }

  // Watch <body> for the engraving-popup-open class. When it appears
  // or disappears, immediately refresh the wired splides so the arrows
  // hide/show without waiting for scroll/resize/timer.
  function watchEngravingPopup() {
    var observer = new MutationObserver(refreshAllArrows);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setTimeout(scan, 400);
      watchEngravingPopup();
    });
  } else {
    setTimeout(scan, 400);
    watchEngravingPopup();
  }

  // Re-run if the product section reloads (Shopify section rendering API).
  document.addEventListener('shopify:section:load', function () {
    setTimeout(scan, 200);
  });

  // Re-run when the window crosses 1024px so it picks up the splide
  // after a desktop resize from mobile.
  var mql = window.matchMedia('(min-width: 1024px)');
  if (mql.addEventListener) mql.addEventListener('change', function () { setTimeout(scan, 200); });
})();